import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { getDb } from './config.js';

// Role → tier mapping (mirrors backend TIER_MAP logic for the roles we capture at onboarding)
export const ROLE_TIER_MAP = {
    ms1_ms2:          'med_student',
    ms3_ms4:          'med_student',
    nursing_student:  'med_student',
    np_pa_aa_student: 'med_student',
    resident_fellow:  'board_review',    // may upgrade to specialty_boards depending on exam target
    np_pa_crna_caa:   'board_review',
    nclex_prep:       'board_review',
    attending_initial:'specialty_boards',
    attending_moc:    'specialty_boards',
    other:            'med_student',
};

// Exam types that require specialty_boards tier even when role is resident
const SPECIALTY_BOARD_EXAM_TYPES = new Set([
    'specialtyBoard', 'internalMedicine', 'surgery', 'obgyn',
    'emergencyMedicine', 'psychiatry', 'dermatology', 'ophthalmology', 'ent',
]);

export function deriveTierFromRoleAndExam(role, examTarget) {
    if (SPECIALTY_BOARD_EXAM_TYPES.has(examTarget)) return 'specialty_boards';
    return ROLE_TIER_MAP[role] || 'med_student';
}

/**
 * Saves the onboarding profile to /users/{uid} in Firestore.
 * Uses merge so other user fields (entitlements, usage) are preserved.
 */
export async function saveOnboardingProfile(uid, profile) {
    if (!uid) return;
    try {
        const db = getDb();
        await setDoc(doc(db, 'users', uid), {
            onboarding: {
                ...profile,
                completedAt: Date.now(),
            },
        }, { merge: true });
    } catch (err) {
        console.warn('[userProfile] failed to save onboarding profile:', err.message);
    }
}

/**
 * Reads the full user document from /users/{uid}.
 * Returns null if document does not exist.
 */
export async function getUserProfile(uid) {
    if (!uid) return null;
    try {
        const db = getDb();
        const snap = await getDoc(doc(db, 'users', uid));
        return snap.exists() ? snap.data() : null;
    } catch (err) {
        console.warn('[userProfile] failed to read user profile:', err.message);
        return null;
    }
}

/**
 * Updates examTarget and tier fields when user changes their focus in Settings.
 * Returns the new tier so the caller can show an upgrade modal if needed.
 */
export async function updateExamTarget(uid, examTarget, role) {
    if (!uid) return null;
    const tier = deriveTierFromRoleAndExam(role, examTarget);
    try {
        const db = getDb();
        await updateDoc(doc(db, 'users', uid), {
            'onboarding.examTarget': examTarget,
            'onboarding.tier': tier,
            'onboarding.updatedAt': Date.now(),
        });
    } catch (err) {
        console.warn('[userProfile] failed to update exam target:', err.message);
    }
    return tier;
}

/**
 * Returns true if the user's current tier can access the requested tier.
 * Hierarchy: med_student < board_review < specialty_boards
 */
export function canAccessTier(userTier, requiredTier) {
    const ORDER = { med_student: 0, board_review: 1, specialty_boards: 2 };
    return (ORDER[userTier] ?? 0) >= (ORDER[requiredTier] ?? 0);
}
